import { useRef, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { useUploadStore } from './uploadStore';
import { useJobData } from '@/shared/hooks/useJobData';
import { UPLOAD_STALE_TIMEOUT_MS } from '@/lib/constants/upload';
import type { JobData } from '@/lib/types/Job';

/**
 * Hook: consumes shared job data, maps job status to upload items,
 * handles stale job deduplication, terminal transitions, and notifications.
 *
 * ENABLED FLAG: Derived from Zustand store items — NOT from drawer open state.
 * Since UploadDrawer uses keepMounted, polling must NOT run just because
 * the drawer was opened once. Only poll when there are actual active uploads.
 *
 * Designed for SSE replacement: swap useJobData internals, keep same return type.
 */
export function useJobPolling(cId: number | null) {
  // Stale job deduplication: track completed job IDs from prior sessions.
  // On first poll, snapshot ALL completed jobIds. Only act on jobs NOT in this set.
  const processedJobIds = useRef<Set<number>>(new Set());
  const initializedRef = useRef(false);

  const items = useUploadStore((s) => s.items);
  const updateItem = useUploadStore((s) => s.updateItem);

  const hasActiveUploads = items.some(
    (i) => i.status === 'creating' || i.status === 'uploading' || i.status === 'processing'
  );

  const { data: jobData, dataUpdatedAt } = useJobData(cId, hasActiveUploads);

  // Process job data in useEffect instead of queryFn.
  // CRITICAL: Depend on dataUpdatedAt, NOT jobData. TanStack Query's structuralSharing
  // returns the same object reference when poll responses are structurally equal.
  // If no jobs change between polls, jobData reference doesn't change and the useEffect
  // wouldn't fire. This would break detectStaleUploads() which must run on every poll
  // tick to check time-since-last-progress. dataUpdatedAt changes on every successful
  // fetch regardless of structural equality.
  useEffect(() => {
    if (jobData) {
      processJobData(jobData.ingestVideoJobs, jobData.transJobs);
      detectStaleUploads();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  function processJobData(ingestJobs: JobData[], transJobs: JobData[]) {
    const currentItems = useUploadStore.getState().items;

    // Initialize on first poll: snapshot all completed jobIds to avoid reprocessing
    if (!initializedRef.current) {
      initializedRef.current = true;
      for (const job of [...ingestJobs, ...transJobs]) {
        if (job.jobStatus === 'completed' || job.jobStatus === 'failed' || job.jobStatus === 'canceled') {
          processedJobIds.current.add(job.jobId);
        }
      }
      return; // Skip processing on first poll (all pre-existing jobs)
    }

    // Map ingest video jobs → upload items (for uploads not yet complete)
    for (const job of ingestJobs) {
      if (processedJobIds.current.has(job.jobId)) continue;
      if (!job.ids.mediaId) continue;

      const item = currentItems.find(
        (i) => i.mediaId > 0 && i.mediaId === job.ids.mediaId
      );
      if (!item) continue;

      if (job.jobStatus === 'completed') {
        // Ingest job complete — item transitions to processing (transcode)
        // Unless it's already in processing or terminal state
        if (item.status === 'uploading' || item.status === 'creating') {
          updateItem(item.id, { status: 'processing', uploadProgress: 100 });
        }
        processedJobIds.current.add(job.jobId);
      } else if (job.jobStatus === 'failed' || job.jobStatus === 'canceled') {
        updateItem(item.id, {
          status: 'error',
          error: `Ingest job ${job.jobStatus}`,
        });
        processedJobIds.current.add(job.jobId);

        notifications.show({
          title: 'Upload failed',
          message: `${item.filename} — ingest job ${job.jobStatus}`,
          color: 'red',
        });
      } else if (job.jobStatus === 'started' || job.jobStatus === 'queued') {
        // Update processing progress from ingest job
        if (item.status === 'processing' && job.completedRate > 0) {
          updateItem(item.id, { processingProgress: job.completedRate });
        }
      }
    }

    // Map transcode jobs → processing progress for items in 'processing' status
    for (const job of transJobs) {
      if (processedJobIds.current.has(job.jobId)) continue;
      if (!job.ids.mediaId) continue;

      // Refresh items after potential updates from ingest jobs above
      const freshItems = useUploadStore.getState().items;
      const item = freshItems.find(
        (i) => i.mediaId > 0 && i.mediaId === job.ids.mediaId
      );
      if (!item) continue;

      if (job.jobStatus === 'completed') {
        updateItem(item.id, {
          status: 'ready',
          processingProgress: 100,
          completedAt: Date.now(),
        });
        processedJobIds.current.add(job.jobId);

        notifications.show({
          title: 'Upload complete',
          message: `${item.filename} is ready`,
          color: 'green',
        });
      } else if (job.jobStatus === 'failed' || job.jobStatus === 'canceled') {
        updateItem(item.id, {
          status: 'error',
          error: `Transcode job ${job.jobStatus}`,
        });
        processedJobIds.current.add(job.jobId);

        notifications.show({
          title: 'Upload failed',
          message: `${item.filename} — transcode ${job.jobStatus}`,
          color: 'red',
        });
      } else if (job.jobStatus === 'started' || job.jobStatus === 'queued') {
        if (item.status === 'processing') {
          updateItem(item.id, { processingProgress: job.completedRate });
        }
      }
    }
  }

  /**
   * Staleness detection: mark uploads as error if no progress for 5 minutes.
   * Catches the edge case where upload dies after sending some bytes but
   * Uppy exhausted all retries.
   */
  function detectStaleUploads() {
    const currentItems = useUploadStore.getState().items;
    const now = Date.now();

    for (const item of currentItems) {
      if (
        item.status === 'uploading' &&
        item.lastProgressAt != null &&
        now - item.lastProgressAt > UPLOAD_STALE_TIMEOUT_MS
      ) {
        updateItem(item.id, {
          status: 'error',
          error: 'Upload stalled — no progress for 5 minutes',
        });
      }
    }
  }
}
